import { useCallback, useEffect, useState } from "react";

import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
    sftpFileReadingSource,
    sftpSource,
} from "helpers/Sources/createSource";

import SwalAlertComp from "components/SwalAlert";

import {
    EMPTY_SFTP_CREDENTIALS,
    FTP_PORT,
    SFTP_PORT,
    SFTP_READ_TYPES,
    SFTP_SOURCE_TYPES,
} from "../SourceCreation.constants";
import {
    areInSameFolder,
    buildFileTree,
    toLeadMappingPayload,
} from "../SourceCreation.helpers";

const EMPTY_CONNECTION = { paths: [] };

const isSftpSourceType = (sourceType) => SFTP_SOURCE_TYPES.includes(sourceType);

/**
 * SFTP / FTP flow: connect with credentials (recaptcha protected), pick a
 * file or directory contents, then create the source and open field mapping.
 */
const useSftpSource = ({
    sourceType,
    language,
    usage,
    leadMappings,
    openMapping,
}) => {
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [credentials, setCredentials] = useState(EMPTY_SFTP_CREDENTIALS);
    const [transferMode, setTransferMode] = useState("default");
    const [isConnected, setIsConnected] = useState(false);
    const [isFormLocked, setIsFormLocked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [connection, setConnection] = useState(EMPTY_CONNECTION);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState("");
    const [treeNodes, setTreeNodes] = useState([]);
    const [treeExpanded, setTreeExpanded] = useState([]);
    const [shouldReconnect, setShouldReconnect] = useState(false);

    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const reset = useCallback(() => {
        setCredentials(EMPTY_SFTP_CREDENTIALS);
        setTransferMode("default");
        setIsConnected(false);
        setIsFormLocked(false);
        setIsLoading(false);
        setConnection(EMPTY_CONNECTION);
        setSelectedFiles([]);
        setSelectedFile("");
        setTreeNodes([]);
        setTreeExpanded([]);
        setShouldReconnect(false);
    }, []);

    useEffect(() => {
        if (isSftpSourceType(sourceType)) reset();
    }, [sourceType, reset]);

    const setCredentialField = useCallback((field, value) => {
        setCredentials((prev) => ({ ...prev, [field]: value }));
    }, []);

    const getPortPayload = () =>
        sourceType === "sftp"
            ? { port: SFTP_PORT, transfer_mode: "default" }
            : { port: FTP_PORT, transfer_mode: transferMode };

    const connect = async () => {
        setIsLoading(true);
        if (!executeRecaptcha) {
            SwalAlertComp({
                title: failedTitle,
                message:
                    language?.source_modal
                        ?.execute_recaptcha_not_yet_available ??
                    "Execute recaptcha not yet available",
                icon: "error",
            });
            setIsLoading(false);
            return;
        }
        const token = await executeRecaptcha("handleSourceSftp");
        if (!token) {
            SwalAlertComp({
                title: failedTitle,
                message:
                    language?.source_modal
                        ?.something_went_wrong_with_recaptcha ??
                    "Something went wrong with Recaptcha",
                icon: "error",
            });
            setIsLoading(false);
            return;
        }

        const response = await sftpSource({
            domain: credentials?.domain,
            username: credentials?.username,
            password: credentials?.password,
            type: credentials?.type,
            source_type: sourceType,
            gRecaptchaResponse: token,
            ...getPortPayload(),
        });
        setIsLoading(false);
        if (response.status) {
            setConnection(response?.data ?? EMPTY_CONNECTION);
            setIsConnected(true);
            setIsFormLocked(true);
            setShouldReconnect(true);
        } else {
            SwalAlertComp({
                title: failedTitle,
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    useEffect(() => {
        if (shouldReconnect) {
            connect();
            setShouldReconnect(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credentials?.type]);

    useEffect(() => {
        const withPath = connection?.files?.withPath;
        if (
            credentials.type === SFTP_READ_TYPES.DIRECTORY &&
            withPath?.length > 0 &&
            withPath.some((item) => item.path === "/")
        ) {
            setTreeNodes(
                buildFileTree([
                    ...(connection?.files?.all ?? []),
                    ...(connection?.paths ?? []),
                ]),
            );
        }
    }, [credentials.type, connection]);

    const createSource = async () => {
        if (isLoading) return;
        setIsLoading(true);

        const payload = {
            type: credentials?.type,
            domain: credentials?.domain,
            username: credentials?.username,
            password: credentials?.password,
            mappings: toLeadMappingPayload(leadMappings),
            usage_types: usage,
            source_type: sourceType,
            ...getPortPayload(),
        };
        if (credentials.type === SFTP_READ_TYPES.FILE) {
            payload.files = [selectedFile];
            payload.path = selectedFile;
        } else {
            payload.files = selectedFiles;
        }

        const response = await sftpFileReadingSource(payload);
        if (response.data !== undefined && response.status) {
            openMapping(
                response?.data?.source,
                sourceType,
                response?.data?.source_type_id,
            );
        } else {
            setIsLoading(false);
            SwalAlertComp({
                title: failedTitle,
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const handleTreeCheck = (checked) => {
        if (checked.length > 1 && !areInSameFolder(checked)) {
            SwalAlertComp({
                title: language?.swal_modal?.warning ?? "Warning!",
                message:
                    language?.checkbox_tree?.folder_control_error ??
                    "Please select only the files within the same folder.",
                icon: "error",
            });
            return;
        }
        setSelectedFiles(checked);
    };

    const hasPaths = connection?.paths?.length > 0;

    return {
        credentials,
        setCredentialField,
        transferMode,
        setTransferMode,
        isConnected,
        isFormLocked,
        isLoading,
        fileOptions: connection?.files?.all ?? [],
        showFileSelect:
            isConnected &&
            credentials.type === SFTP_READ_TYPES.FILE &&
            hasPaths,
        showDirectoryTree:
            isConnected &&
            credentials.type === SFTP_READ_TYPES.DIRECTORY &&
            hasPaths,
        selectedFiles,
        selectedFile,
        setSelectedFile,
        treeNodes,
        treeExpanded,
        setTreeExpanded,
        handleTreeCheck,
        connect,
        createSource,
        reset,
    };
};

export default useSftpSource;
